/**
 * functions.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { assertArray, assertIndexInto } from "./assertions";

export function compose<A, B, C> (f : (b : B) => C, g : (a : A) => B)
  : (a : A) => C
{
  return (a : A) => f(g(a));
}

export function identity<T> (x : T) : T
{
  return x;
}

export function inverse (x : any) : boolean
{
  return !x;
}

export function inverted<T> (fn : (t : T) => any) : (t : T) => boolean
{
  return (t : T) => (inverse(fn(t)));
}

/**
 * oneline is a template literal tag that converts newlines+indentation spaces
 * into a single space. It's intended to be used as other template tags, ex:
 * oneline`template text here`
 * @param literals The non-interpolated string chunks
 * @param keys The values to be interpolated into the template
 */
export function oneline (literals : TemplateStringsArray, ...keys : any[])
  : string
{
  return literals.map(
    (literal : string, i : number) =>
      (i < keys.length) ? (literal + `${keys[i]}`) : literal
      // just replicate a "standard" template literal interpolation
  ).join("").replace(/\n\s*/g, " ").replace(/^\s*/, "");
}

export function arrayRemove<T> (arr : T[], item : any) : T[]
{
  assertArray(arr);
  const i : number = arr.indexOf(item);
  if (i === -1)
  {
    throw new Error("Can only remove entries that exist in array");
  }
  arr.splice(i, 1);
  return arr;
}

export function arraySwap<T> (arr : T[], i : number, j : number = (i + 1)) : T[]
{
  assertArray(arr);
  assertIndexInto(i, arr);
  assertIndexInto(j, arr);
  const swapped : T = arr[i];
  arr[i] = arr[j];
  arr[j] = swapped;
  return arr;
}

export function arrayIntersects<T> (a : T[], b : T[]) : boolean
{
  return a.some((x : T) => b.indexOf(x) > -1);
}

export function arraySubset<T> (sub : T[], sup : T[]) : boolean
{
  return sub.every((val : T) => sup.indexOf(val) > -1);
}

export function deepCopyViaMarshal<T> (x : T) : T
{
  return JSON.parse(JSON.stringify(x));
}

export function sequentialPairs<T> (arr : T[]) : Array<[T, T]>
{
    return arr.reduce(
        (prev : Array<[T, T]>, curr : T, i : number) => {
            if (i % 2 === 0)
            {
                prev.push([curr, null]);
            }
            else
            {
                prev.slice(-1)[0][1] = curr;
            }
            return prev;
        },
        [] as Array<[T, T]>
    );
}

export function setEquals<T> (a : Set<T>, b : Set<T>) : boolean
{
  if (a === b)
  {
    return true;
  }
  if (a === null || b === null || a.size !== b.size)
  {
    return false;
  }
  return Array.from(a).every((t : T) => b.has(t));
}

// export function propSubtractor<T> (prop : string) : (a : T, b : T) => number
// {
//   return (a : T, b : T) => ((a as any)[prop] - (b as any)[prop]);
// }

export function textualList (items : string[], conj : string = "and") : string
{
  if (items.length < 2)
  {
    return items.join("");
  }
  if (items.length === 2)
  {
    return `${items[0]} ${conj} ${items[1]}`;
  }
  return items.map(
    (str : string, i : number) =>
      (i === items.length - 1) ? `${conj} ${str}` : `${str}, `
    ).join("");
}

export function escapeForHTML (text : string) : string
{
  return (
    String(text)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  );
}

// // is TS2.2 this could be the predicate "index is keyof<arr>"?
// export function validIndexFor (arr : {length : number}, index : number)
//   : boolean
// {
//   if (!Number.isInteger(index))
// }

export function insertFactory<T> (compare : (a : T, b : T) => number)
  : (arr : T[], item : T) => T[]
{
  return (arr : any[], item : any) => {
    let i : number;
    for (i = 0; i < arr.length; i++)
    {
      if (compare(item, arr[i]) < 0)
      {
        break;
      }
    }
    arr.splice(i, 0, item);
    return arr;
  };
}

type Valueless = null | undefined;
type Inputless = Valueless | "";
type Empty = Inputless | {} | (any[] & {length : 0});
// the zero-length assertion probably doesn't work

/**
 * Determine if a value is either null or undefined.
 * @param  {any}     obj
 * @return {boolean}
 */
export function valueless (obj : any) : obj is Valueless
{
  return (obj === null || obj === undefined);
}

export function valueDefault<T> (obj : T | Valueless, fallback : T) : T
{
  if (valueless(obj))
  {
    return fallback;
  }
  return obj;
}

/**
 * Determine if a value is null, undefined, or the empty string.
 * @param  {any}     obj
 * @return {boolean}
 */
export function inputless (obj : any) : obj is Inputless
{
  return valueless(obj) || obj === "";
}

function emptyObject (obj : any) : obj is {}
{
  return (
    typeof obj === "object"
    && obj !== null
    && !(obj instanceof Date)
    && Object.keys(obj).length === 0
  );
}
function emptyArray (obj : any) : boolean
{
  return (Array.isArray(obj) && obj.length === 0);
}

/**
 * Determine if a value is null, undefined, or an empty string, array, or object
 */
export function empty (obj : any) : obj is Empty
{
  return inputless(obj) || emptyArray(obj) || emptyObject(obj);
}

/**
 * Determine if a string is a stringified form of a number.
 * @param  {string}  str
 * @return {boolean}
 */
export function isNumberString (str : string) : boolean
{
  return (str === ((+str) + ""));
}

/**
 * Determine if a string is a stringified form of an integer.
 * @param  {string}  str
 * @return {boolean}
 */
export function isIntegerString (str : string) : boolean
{
  return (str === (Number.parseInt(str) + ""));
}

export function assert (test : boolean, message? : string) : void
{
  if (!test)
  {
    message = message || "assertion failed";
    throw new Error(message);
  }
}

export function partition<T> (
  arr : T[],
  separator : (item : T) => boolean
) : [T[], T[]]
{
    const trues : T[] = [];
    const falses : T[] = [];
    arr.forEach((value : T) => {
      if (separator(value))
      {
        trues.push(value);
      }
      else
      {
        falses.push(value);
      }
    });
    return [trues, falses];
}

export function randomNoun () : string
{
  const words : string[] = [
    "cat", "dog", "fish", "microscope", "compass",
    "spectrometer", "thunder", "hail", "sleet", "oceans", "deserts",
    "arroyo", "adage", "suggestion", "nostalgia", "lemongrass",
    "pitcher plant", "ghostpipe", "rhythm", "groove", "pocket", "gibberish",
    "chalkdust", "golf magazine", "wine glass", "t-shirt", "bolo tie",
    "stockings", "antacid", "Antarctica", "penguin", "table saw",
    "tennis racket", "murder", "laser", "chess board", "frog", "thermometer",
    "crop rotation", "rufous-throated honeyeater", "sparkle", "dull pencil",
    "fire extinguisher", "real estate agent", "riot", "poet"
  ];
  return words[Math.floor(Math.random() * words.length)];
}

export function randomInt (limit : number) : number
{
  return Math.floor(Math.random() * limit);
}

/**
 * Convert to an arbitrary object to JSON, with some additional niceties over
 * the default conversion.
 * @param {any} o The object to be converted
 * @param {number}  objLimit  The maximum number of objects to descend into
 * during the JSONification process, after which further objects are simply
 * converted as a static string and not "opened".
 * @param {boolean} skipUnderscores Whether to entirely ignore any keys that
 * start with "_".
 */
export function jsonify (
  o : any,
  objLimit : number = 1000,
  skipUnderscores : boolean = false
) : string
{
  let objCount : number = 0;
  const seenObjects : WeakSet<any> = new WeakSet();

  return JSON.stringify(o, (key : string, value : string) => {
    if (skipUnderscores && key[0] === "_")
    {
      return;
    }
    if (typeof value === "object" && value !== null)
    {
      if (seenObjects.has(value))
      {
        return "<circular>";
      }
      objCount++;
      if (objCount > objLimit)
      {
        return "<object limit>";
      }

      try
      {
        seenObjects.add(value);
      }
      catch (err)
      {
        console.error("Could not add to weak set:", value);
        return "weakset error";
      }
    }
    return value;
  });
}

export function assignExcept (
  target : any,
  source : any,
  exclusions : string[]
) : void
{
  for (const key of Object.keys(source))
  {
    if (exclusions.indexOf(key) === -1)
    {
      target[key] = source[key];
    }
  }
}
export function assignOnly (
  target : any,
  source : any,
  inclusions : string[]
) : void
{
  for (const key of Object.keys(source))
  {
    if (inclusions.indexOf(key) > -1)
    {
      target[key] = source[key];
    }
  }
}

export function dot (object : any, path : string) : any
{
  if (typeof path !== "string")
  {
    return object;
  }
  const segments : string[] = path.split(".");
  let seg : string;
  for (seg of segments)
  {
    if (typeof object !== "object")
    {
      return null;
    }
    object = object[seg];
  }
  return object;
}

export function hardEquals<T> (comparator : T) : (x : T) => boolean
{
  return (x : T) => (x === comparator);
}

export function getOrInit<K, V> (map: Map<K, V>, key: K, init: V) {
  if (map.has(key)) {
    return map.get(key);
  } else {
    map.set(key, init);
    return init;
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/support/utilities/functions.ts